import { UserContext } from "../contexts/UserContext";
import { UrlContext } from "../contexts/UrlContext";
import React, { useState, useEffect, useContext } from "react";

export default function TestingTasks() {
  const [pendingTasks, setPendingTasks] = useState([]);
  const [reviewTasks, setReviewTasks] = useState([]);
  const [readyForProduction, setReadyForProduction] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [feedback, setFeedback] = useState({});
  const { API_URL } = useContext(UrlContext);
  const { userData } = useContext(UserContext);
  const username = userData?.username;

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const [pendingResponse, reviewResponse, productionResponse] =
        await Promise.all([
          fetch(`${API_URL}pending-tasks`),
          fetch(`${API_URL}review-tasks`),
          fetch(`${API_URL}production-tasks`),
        ]);

      const pending = await pendingResponse.json();
      const review = await reviewResponse.json();
      const production = await productionResponse.json();
      setPendingTasks(sortTasksByDate(pending));
      setReviewTasks(sortTasksByDate(review));
      setReadyForProduction(sortTasksByDate(production));
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };
  console.log(pendingTasks);

  const sortTasksByDate = (tasks) => {
    return tasks.sort(
      (a, b) =>
        new Date(b.date + " " + b.time) - new Date(a.date + " " + a.time)
    );
  };

  const handleTaskStatus = async (taskId, status) => {
    if (status === "review") {
      setPendingTasks(
        pendingTasks.map((task) =>
          task.id === taskId ? { ...task, status } : task
        )
      );
    } else if (status === "approved") {
      try {
        const response = await fetch(`${API_URL}update-task-status`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ taskId, status }),
        });

        if (response.ok) {
          const approvedTask = pendingTasks.find((task) => task.id === taskId);
          setReadyForProduction([...readyForProduction, approvedTask]);
          setPendingTasks(pendingTasks.filter((task) => task.id !== taskId));
        } else {
          console.error("Error updating task status");
        }
      } catch (error) {
        console.error("Error updating task status:", error);
      }
    }
  };

  const handleFeedback = async (taskId) => {
    if (!feedback[taskId]) return;

    try {
      const response = await fetch(`${API_URL}add-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ taskId, feedback: feedback[taskId] }),
      });

      if (response.ok) {
        const updatedTask = pendingTasks.find((task) => task.id === taskId);
        if (updatedTask) {
          setReviewTasks([
            ...reviewTasks,
            { ...updatedTask, feedback: feedback[taskId] },
          ]);
          setPendingTasks(pendingTasks.filter((task) => task.id !== taskId));
        }
        setFeedback({ ...feedback, [taskId]: "" });
      } else {
        console.error("Error adding feedback");
      }
    } catch (error) {
      console.error("Error adding feedback:", error);
    }
  };

  const handleAddNewTask = async () => {
    if (newTask.trim() === "") return;

    try {
      const response = await fetch(`${API_URL}add-task`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ task: newTask, user: username }),
      });

      if (response.ok) {
        setNewTask("");
        fetchTasks();
      } else {
        console.error("Error adding new task");
      }
    } catch (error) {
      console.error("Error adding new task:", error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const response = await fetch(`${API_URL}complete-task`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ taskId }),
      });

      if (response.ok) {
        setReadyForProduction(
          readyForProduction.filter((task) => task.id !== taskId)
        );
      } else {
        console.error("Error completing task");
      }
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const handleApproveReviewedTask = async (taskId) => {
    try {
      const response = await fetch(`${API_URL}approve-reviewed-task`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ taskId }),
      });

      if (response.ok) {
        // Instead of manually updating the state, we'll fetch all tasks again
        // to ensure our frontend state is in sync with the backend
        fetchTasks();
      } else {
        console.error("Error approving reviewed task");
      }
    } catch (error) {
      console.error("Error approving reviewed task:", error);
    }
  };

  return (
    <div className="mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-center mb-8 text-blue-600 uppercase tracking-wide">
        Pending Tasks for Testing
      </h1>
      <table className="w-full bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <thead className="bg-blue-500 text-white">
          <tr>
            <th className="py-3 px-4 text-left w-3/5">Description</th>
            <th className="py-3 px-4 text-left w-1/10">Created by</th>
            <th className="py-3 px-4 text-left w-1/10">Date</th>
            <th className="py-3 px-4 text-left w-1/5">Action</th>
          </tr>
        </thead>
        <tbody>
          {pendingTasks.map((task) => (
            <React.Fragment key={task.id}>
              <tr className="border-b border-gray-200 hover:bg-gray-100">
                <td className="py-3 px-4 w-3/5">{task.task}</td>
                <td className="py-3 px-4 w-1/10">{task.user}</td>
                <td className="py-3 px-4 w-1/10">
                  {task.date} {task.time}
                </td>
                <td className="py-3 px-4 w-1/5">
                  <div className="flex flex-col space-y-2">
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio text-blue-600"
                          name={`status-${task.id}`}
                          value="approved"
                          checked={task.status === "approved"}
                          onChange={() => handleTaskStatus(task.id, "approved")}
                        />
                        <span className="ml-2">Approved</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio text-red-600"
                          name={`status-${task.id}`}
                          value="review"
                          checked={task.status === "review"}
                          onChange={() => handleTaskStatus(task.id, "review")}
                        />
                        <span className="ml-2">Review</span>
                      </label>
                    </div>
                  </div>
                </td>
              </tr>
              {task.status === "review" && (
                <tr className="bg-red-50">
                  <td colSpan="4" className="py-2 px-4">
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={feedback[task.id] || ""}
                        onChange={(e) =>
                          setFeedback({
                            ...feedback,
                            [task.id]: e.target.value,
                          })
                        }
                        placeholder="Provide feedback"
                        className="flex-grow px-2 py-1 border rounded"
                      />
                      <button
                        onClick={() => handleFeedback(task.id)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Send
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <div className="mb-8 flex space-x-4">
        <input
          type="text"
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          placeholder="New Task"
          className="flex-grow px-4 py-2 border rounded"
          aria-label="New task description"
        />
        <button
          onClick={handleAddNewTask}
          className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Add
        </button>
      </div>

      <h2 className="text-3xl font-bold text-center mb-6 text-red-600 uppercase tracking-wide">
        Tasks that need to be reviewed
      </h2>
      <table className="w-full bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <thead className="bg-red-500 text-white">
          <tr>
            <th className="py-3 px-4 text-left w-1/3">Description</th>
            <th className="py-3 px-4 text-left w-1/6">Created by</th>
            <th className="py-3 px-4 text-left w-1/6">Date</th>
            <th className="py-3 px-4 text-left w-1/4">Feedback</th>
            <th className="py-3 px-4 text-left w-1/12">Action</th>
          </tr>
        </thead>
        <tbody>
          {reviewTasks.map((task) => (
            <tr
              key={task.id}
              className="border-b border-gray-200 hover:bg-gray-100"
            >
              <td className="py-3 px-4">{task.task}</td>
              <td className="py-3 px-4">{task.user}</td>
              <td className="py-3 px-4">
                {task.date} {task.time}
              </td>
              <td className="py-3 px-4">{task.feedback}</td>
              <td className="py-3 px-4">
                <button
                  onClick={() => handleApproveReviewedTask(task.id)}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Send
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="text-3xl font-bold text-center mb-6 text-green-600 uppercase tracking-wide">
        Ready for Production
      </h2>
      <table className="w-full bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <thead className="bg-green-500 text-white">
          <tr>
            <th className="py-3 px-4 text-left w-3/5">Description</th>
            <th className="py-3 px-4 text-left w-1/10">Created by</th>
            <th className="py-3 px-4 text-left w-1/10">Date</th>
            <th className="py-3 px-4 text-left w-1/5">Action</th>
          </tr>
        </thead>
        <tbody>
          {readyForProduction.map((task) => (
            <tr
              key={task.id}
              className="border-b border-gray-200 hover:bg-gray-100"
            >
              <td className="py-3 px-4 w-3/5">{task.task}</td>
              <td className="py-3 px-4 w-1/10">{task.user}</td>
              <td className="py-3 px-4 w-1/10">
                {task.date} {task.time}
              </td>
              <td className="py-3 px-4 w-1/5">
                <button
                  onClick={() => handleCompleteTask(task.id)}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Completed
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
